import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMoney, moneyFromCents } from '@wodi/domain';
import type { PharmacyItemView, SalePreviewView, StoreView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  DateField,
  EmptyState,
  LoadingBlock,
  MoneyField,
  Notice,
  Row,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useRef, useState, type SetStateAction } from 'react';

import { billingKeys, currentShiftQuery } from '../api/billing.ts';
import { createSale, pharmacyKeys, previewSale, salesQuery } from '../api/pharmacy.ts';
import { METHOD_LABELS, money, type PaymentMethodKey } from '../billing/format.ts';
import { ReceiptPrinter } from '../billing/receipt.tsx';
import { sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { deviceDraftKey, useDeviceDraft } from '../offline/use-device-draft.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { PatientSearchPicker } from '../patients/patient-search-palette.tsx';
import { formatIsoDate, formatTimeOnly } from '../patients/format.ts';
import { screen, ShortcutHint, TableScroll } from '../layout/screen.tsx';
import { WithPharmacyStore } from '../pharmacy/common.tsx';
import { PharmacyItemPicker } from '../pharmacy/item-picker.tsx';
import styles from '../pharmacy/pharmacy.module.css';
import { errorText, newRequestKey } from '../stock/common.tsx';
import { formatQuantity, readQuantity } from '../stock/format.ts';
import { toasts } from '../toasts.ts';
import { OpenShiftDialog } from './cashier.tsx';

interface CartLine {
  item: PharmacyItemView;
  quantityText: string;
  packCode: string | null;
  instructions: string;
}

interface TenderDraft {
  method: PaymentMethodKey;
  cents: bigint | null;
  reference: string;
}

interface OutsidePrescription {
  patientId: string | null;
  prescriberName: string;
  prescriberCouncil: string;
  prescriberRegistrationNumber: string;
  issuingFacilityName: string;
  prescribedOn: string | null;
}

const EMPTY_RX: OutsidePrescription = {
  patientId: null,
  prescriberName: '',
  prescriberCouncil: '',
  prescriberRegistrationNumber: '',
  issuingFacilityName: '',
  prescribedOn: null,
};

interface CounterDraft {
  cart: CartLine[];
  rx: OutsidePrescription;
  patientText: string;
}

const EMPTY_COUNTER: CounterDraft = { cart: [], rx: EMPTY_RX, patientText: '' };

function acceptCounterDraft(stored: unknown): CounterDraft | null {
  const draft = stored as Partial<CounterDraft> | null;
  if (draft === null || !Array.isArray(draft.cart) || draft.cart.length === 0) return null;
  return {
    cart: draft.cart,
    rx: { ...EMPTY_RX, ...draft.rx },
    patientText: draft.patientText ?? '',
  };
}

/** A setter for one part of the counter draft, shaped like useState's. */
function counterDraftSetter<K extends keyof CounterDraft>(
  setDraft: (next: SetStateAction<CounterDraft>) => void,
  part: K,
) {
  return (next: SetStateAction<CounterDraft[K]>) => {
    setDraft((d) => ({
      ...d,
      [part]: typeof next === 'function' ? next(d[part]) : next,
    }));
  };
}

const REFERENCE_LABEL: Record<Exclude<PaymentMethodKey, 'cash'>, string> = {
  mpesa: 'M-Pesa code',
  card: 'Card slip reference',
  bank_transfer: 'Bank reference',
};

/**
 * The walk-in counter sale (dispensing plan §8.2; ADR 0014): search or scan, build the
 * cart, take payment in full, print the receipt, in one screen. Over-the-counter items
 * sell to anyone; prescription-only items need an outside prescription recorded against
 * an identified patient. Ctrl/⌘Enter takes payment, `/` goes to the search.
 */
export function PharmacyCounterPage() {
  return (
    <WithPharmacyStore title="Counter sale">
      {(store) => <Counter key={store.id} store={store} />}
    </WithPharmacyStore>
  );
}

function Counter({ store }: { store: StoreView }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const searchRef = useRef<HTMLInputElement>(null);
  // The cart and outside prescription are kept on this device (ADR 0018); tenders never are.
  const { data: session } = useQuery(sessionQuery);
  const counterDraft = useDeviceDraft<CounterDraft>(
    deviceDraftKey('counter-sale', session ?? null, store.id),
    EMPTY_COUNTER,
    { accept: acceptCounterDraft },
  );
  const { cart, rx, patientText } = counterDraft.value;
  const setCart = counterDraftSetter(counterDraft.setValue, 'cart');
  const setRx = counterDraftSetter(counterDraft.setValue, 'rx');
  const setPatientText = counterDraftSetter(counterDraft.setValue, 'patientText');
  const [tenders, setTenders] = useState<TenderDraft[]>([
    { method: 'cash', cents: null, reference: '' },
  ]);
  const [requestKey, setRequestKey] = useState(newRequestKey);
  const [tried, setTried] = useState(false);
  const [receiptIds, setReceiptIds] = useState<string[]>([]);
  const shift = useQuery(currentShiftQuery);
  const [openingShift, setOpeningShift] = useState(false);

  useShortcut('/', () => searchRef.current?.focus(), { description: 'Find a medicine' });

  const lines = cart.map((l) => ({ ...l, quantity: readQuantity(l.quantityText) }));
  const needsRx = cart.some((l) => l.item.supplyClass === 'prescription_only');
  const previewLines = lines
    .filter((l) => (l.quantity ?? 0) > 0)
    .map((l) => ({ serviceItemId: l.item.id, quantity: l.quantity ?? 0 }));
  const preview = useQuery({
    queryKey: ['pharmacy', 'sale-preview', store.id, previewLines],
    queryFn: () => previewSale({ storeId: store.id, lines: previewLines }),
    enabled: online && previewLines.length > 0,
    placeholderData: keepPreviousData,
    retry: false,
  });
  const total = preview.data === undefined ? null : BigInt(preview.data.total.cents);
  const tendered = tenders.reduce((sum, t) => sum + (t.cents ?? 0n), 0n);
  const cashChange =
    total !== null && tenders.length === 1 && tenders[0]?.method === 'cash' && tendered > total
      ? tendered - total
      : null;

  const problems: string[] = [];
  if (cart.length === 0) problems.push('Add at least one medicine.');
  if (lines.some((l) => (l.quantity ?? 0) <= 0)) problems.push('Enter a quantity on every line.');
  if (needsRx) {
    if (rx.patientId === null) problems.push('Find the patient for the outside prescription.');
    if (
      rx.prescriberName.trim() === '' ||
      rx.prescriberCouncil.trim() === '' ||
      rx.prescriberRegistrationNumber.trim() === '' ||
      rx.issuingFacilityName.trim() === '' ||
      rx.prescribedOn === null
    )
      problems.push(
        'Fill in the outside prescription: prescriber, council, number, facility, date.',
      );
    if (
      lines.some(
        (l) => l.item.supplyClass === 'prescription_only' && l.instructions.trim().length < 3,
      )
    )
      problems.push('Type the instructions for each prescription-only medicine.');
  }
  if (preview.data !== undefined && !preview.data.ready)
    problems.push('Some lines can’t be sold as they are. See the cart.');
  if (tenders.some((t) => t.cents === null || t.cents <= 0n))
    problems.push('Enter the amount received.');
  if (tenders.some((t) => t.method !== 'cash' && t.reference.trim().length < 3))
    problems.push('Enter the payment reference.');
  if (total !== null && tendered < total)
    problems.push(`A counter sale is paid in full: ${formatMoney(moneyFromCents(total))}.`);

  const sale = useMutation({
    mutationFn: () =>
      createSale({
        clientRequestId: requestKey,
        storeId: store.id,
        ...(needsRx && rx.patientId !== null && rx.prescribedOn !== null
          ? {
              patientId: rx.patientId,
              externalPrescription: {
                prescriberName: rx.prescriberName.trim(),
                prescriberCouncil: rx.prescriberCouncil.trim(),
                prescriberRegistrationNumber: rx.prescriberRegistrationNumber.trim(),
                issuingFacilityName: rx.issuingFacilityName.trim(),
                prescribedOn: rx.prescribedOn,
              },
            }
          : {}),
        lines: lines.map((l) => ({
          serviceItemId: l.item.id,
          quantity: l.quantity ?? 0,
          ...(l.packCode === null ? {} : { packCode: l.packCode }),
          ...(l.item.supplyClass === 'prescription_only'
            ? { instructions: l.instructions.trim() }
            : {}),
        })),
        tenders: tenders.map((t) =>
          t.method === 'cash'
            ? { method: 'cash' as const, tenderedCents: (t.cents ?? 0n).toString() }
            : {
                method: t.method,
                amountCents: (t.cents ?? 0n).toString(),
                reference: t.reference.trim(),
              },
        ),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: pharmacyKeys.all });
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      toasts.show({
        title: 'Payment received',
        description:
          result.change !== null && result.change.cents !== '0'
            ? `${result.saleNumber}. Change ${money(result.change)}`
            : result.saleNumber,
      });
      setReceiptIds(result.receipts.map((r) => r.id));
      await counterDraft.clear();
      setTenders([{ method: 'cash', cents: null, reference: '' }]);
      setRequestKey(newRequestKey());
      setTried(false);
      searchRef.current?.focus();
    },
  });

  const go = () => {
    setTried(true);
    if (problems.length === 0 && online && !sale.isPending && shift.data?.shift != null)
      sale.mutate();
  };
  useShortcut('mod+enter', go, { allowInFields: true, description: 'Take payment' });

  const add = (item: PharmacyItemView) => {
    setReceiptIds([]);
    const step = item.scanned?.baseUnitsPerPack ?? 1;
    setCart((all) => {
      const existing = all.find((l) => l.item.id === item.id);
      if (existing === undefined)
        return [
          ...all,
          {
            item,
            quantityText: String(step),
            packCode: item.scanned?.packUnitCode ?? null,
            instructions: '',
          },
        ];
      return all.map((l) =>
        l.item.id === item.id
          ? { ...l, quantityText: String((readQuantity(l.quantityText) ?? 0) + step) }
          : l,
      );
    });
  };

  return (
    <div className={styles.counter}>
      <div className={styles.section}>
        <PharmacyItemPicker storeId={store.id} onSelect={add} inputRef={searchRef} autoFocus />
        {cart.length === 0 ? (
          <EmptyState>
            The cart is empty. Search or scan a medicine to add it. Press / to search.
          </EmptyState>
        ) : (
          <CartTable
            lines={cart}
            preview={preview.data}
            onChange={(id, change) => {
              setCart((all) => all.map((l) => (l.item.id === id ? { ...l, ...change } : l)));
            }}
            onRemove={(id) => {
              setCart((all) => all.filter((l) => l.item.id !== id));
            }}
          />
        )}
        {preview.isError ? (
          <Notice tone="error">
            {errorText(preview.error, "The prices couldn't be worked out. Try again.")}
          </Notice>
        ) : null}
        {cart.some((l) => l.item.isControlled) ? (
          <Notice tone="error">
            Controlled drugs are dispensed only against a prescription from this facility. Remove
            them from the sale.
          </Notice>
        ) : null}
        {needsRx ? (
          <OutsidePrescriptionForm
            rx={rx}
            onChange={(change) => {
              setRx((r) => ({ ...r, ...change }));
            }}
            patientText={patientText}
            onPatientText={setPatientText}
            tried={tried}
          />
        ) : null}
        <TodaySales storeId={store.id} />
      </div>

      <aside className={`${styles.section} ${screen.panel}`} aria-label="Payment">
        <h2 className={styles.sectionHeading}>Payment</h2>
        <p className={styles.total} aria-live="polite">
          {total === null ? 'KES 0' : formatMoney(moneyFromCents(total))}
        </p>
        {shift.isPending ? <LoadingBlock label="Checking your shift" rows={1} /> : null}
        {shift.isError ? (
          <Notice tone="error">{errorText(shift.error, "Your shift couldn't be checked.")}</Notice>
        ) : null}
        {shift.isSuccess && shift.data.shift === null ? (
          <Notice
            tone="info"
            action={
              <Button
                dense
                onPress={() => {
                  setOpeningShift(true);
                }}
              >
                Open shift
              </Button>
            }
          >
            Open a cashier shift to take payment at the counter.
          </Notice>
        ) : null}
        {shift.isSuccess && shift.data.shift !== null ? (
          <p className={styles.meta}>
            <span>Shift </span>
            <span className="wodi-id">{shift.data.shift.number}</span>
            <span> open</span>
          </p>
        ) : null}
        {tenders.map((t, index) => (
          <div key={index} className={styles.form}>
            <div className={styles.row}>
              <Select
                label="Method"
                options={(Object.keys(METHOD_LABELS) as PaymentMethodKey[]).map((m) => ({
                  id: m,
                  label: METHOD_LABELS[m],
                }))}
                value={t.method}
                onChange={(v) => {
                  setTenders((all) =>
                    all.map((x, i) => (i === index ? { ...x, method: v as PaymentMethodKey } : x)),
                  );
                }}
              />
              <MoneyField
                label={t.method === 'cash' ? 'Cash received' : 'Amount'}
                value={t.cents}
                onChange={(value) => {
                  setTenders((all) =>
                    all.map((x, i) => (i === index ? { ...x, cents: value } : x)),
                  );
                }}
              />
            </div>
            {t.method === 'cash' ? null : (
              <TextField
                label={REFERENCE_LABEL[t.method]}
                value={t.reference}
                onChange={(value) => {
                  setTenders((all) =>
                    all.map((x, i) => (i === index ? { ...x, reference: value } : x)),
                  );
                }}
              />
            )}
            {tenders.length > 1 ? (
              <Button
                dense
                variant="quiet"
                onPress={() => {
                  setTenders((all) => all.filter((_, i) => i !== index));
                }}
              >
                Remove this method
              </Button>
            ) : null}
          </div>
        ))}
        {tenders.length < 4 ? (
          <div>
            <Button
              dense
              onPress={() => {
                setTenders((all) => [...all, { method: 'mpesa', cents: null, reference: '' }]);
              }}
            >
              Add another method
            </Button>
          </div>
        ) : null}
        {cashChange === null ? null : (
          <p className={styles.change}>{`Change ${formatMoney(moneyFromCents(cashChange))}`}</p>
        )}
        {tried && problems.length > 0 ? (
          <Notice tone="error">
            <ul>
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Notice>
        ) : null}
        {sale.isError ? (
          <Notice tone="error">
            {errorText(sale.error, "The sale wasn't recorded. Nothing was taken. Try again.")}
          </Notice>
        ) : null}
        {online ? null : (
          <Notice tone="info">
            {`Selling needs a connection to Wodi.${counterDraft.available ? ' The cart is kept on this device.' : ''} Write a receipt from the receipt book and enter the sale when Wodi is back.`}
          </Notice>
        )}
        <Button
          variant="primary"
          onPress={go}
          isPending={sale.isPending}
          pendingLabel="Taking payment"
          isDisabled={!online || shift.data?.shift == null}
          shortcut="mod+enter"
        >
          Take payment
        </Button>
        <ShortcutHint combo="mod+enter" action="take payment" />
        {receiptIds.map((id) => (
          <div key={id} className={styles.receiptPreview}>
            <ReceiptPrinter receiptId={id} auto />
          </div>
        ))}
      </aside>
      {openingShift ? (
        <OpenShiftDialog
          onClose={() => {
            setOpeningShift(false);
          }}
        />
      ) : null}
    </div>
  );
}

function CartTable({
  lines,
  preview,
  onChange,
  onRemove,
}: {
  lines: CartLine[];
  preview: SalePreviewView | undefined;
  onChange: (id: string, change: Partial<CartLine>) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className={styles.section}>
      <h2 className={styles.sectionHeading}>Cart</h2>
      {lines.map((line) => {
        const p = preview?.lines.find((x) => x.serviceItemId === line.item.id);
        const quantity = readQuantity(line.quantityText);
        return (
          <fieldset key={line.item.id} className={styles.line}>
            <legend className={styles.lineHeading}>
              <span>{line.item.name}</span>
              <span className="wodi-num">{p?.lineTotal == null ? '' : money(p.lineTotal)}</span>
            </legend>
            <div className={styles.toolbar}>
              <div className={styles.quantity}>
                <TextField
                  label="Quantity"
                  unit={line.item.baseUnitDisplay}
                  inputMode="numeric"
                  value={line.quantityText}
                  onChange={(text) => {
                    onChange(line.item.id, { quantityText: text });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === '+' || e.key === '-') {
                      e.preventDefault();
                      const next = Math.max(1, (quantity ?? 0) + (e.key === '+' ? 1 : -1));
                      onChange(line.item.id, { quantityText: String(next) });
                    }
                  }}
                  {...(quantity === null || quantity <= 0
                    ? { errorMessage: 'Enter a whole number.' }
                    : {})}
                />
              </div>
              {line.item.supplyClass === 'prescription_only' ? (
                <Tag state="pending">Prescription only</Tag>
              ) : null}
              <Button
                variant="quiet"
                onPress={() => {
                  onRemove(line.item.id);
                }}
              >
                Remove
              </Button>
            </div>
            {p === undefined ? null : (
              <p className={styles.meta}>
                {p.batches.length === 0
                  ? `${formatQuantity(p.usable, line.item.baseUnitDisplay)} in stock`
                  : p.batches
                      .map(
                        (b, i) =>
                          `Batch ${b.batchNumber}${b.expiryDate === null ? '' : `, expires ${formatIsoDate(b.expiryDate)}`}${i === 0 ? ' (earliest expiry)' : ''}: ${formatQuantity(b.quantity, line.item.baseUnitDisplay)}`,
                      )
                      .join('; ')}
                {p.unitPrice === null ? '' : `. ${money(p.unitPrice)} each`}
              </p>
            )}
            {p?.problem == null ? null : <Notice tone="error">{p.problem}</Notice>}
            {line.item.supplyClass === 'prescription_only' ? (
              <TextField
                label="Instructions for the label"
                description="As written on the outside prescription."
                value={line.instructions}
                onChange={(text) => {
                  onChange(line.item.id, { instructions: text });
                }}
              />
            ) : null}
          </fieldset>
        );
      })}
    </div>
  );
}

function OutsidePrescriptionForm({
  rx,
  onChange,
  patientText,
  onPatientText,
  tried,
}: {
  rx: OutsidePrescription;
  onChange: (change: Partial<OutsidePrescription>) => void;
  patientText: string;
  onPatientText: (text: string) => void;
  tried: boolean;
}) {
  const need = (value: string, message: string) =>
    tried && value.trim() === '' ? { errorMessage: message } : {};
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.sectionHeading}>Record an outside prescription</legend>
      <p className={styles.meta}>
        Prescription-only medicines are sold only against a prescription, to an identified patient.
        Wodi records what you type; you confirm the prescription is valid by selling.
      </p>
      {rx.patientId === null ? (
        <PatientSearchPicker
          inputValue={patientText}
          onInputChange={onPatientText}
          onSelect={(result) => {
            onPatientText('');
            onChange({ patientId: result.id });
          }}
        />
      ) : (
        <div className={styles.section}>
          <PatientBannerLoader patientId={rx.patientId} />
          <div>
            <Button
              dense
              variant="quiet"
              onPress={() => {
                onChange({ patientId: null });
              }}
            >
              Choose another patient
            </Button>
          </div>
        </div>
      )}
      <div className={styles.row}>
        <TextField
          label="Prescriber"
          value={rx.prescriberName}
          onChange={(v) => {
            onChange({ prescriberName: v });
          }}
          {...need(rx.prescriberName, 'Enter the prescriber’s name.')}
        />
        <TextField
          label="Council"
          description="For example KMPDC or COC."
          value={rx.prescriberCouncil}
          onChange={(v) => {
            onChange({ prescriberCouncil: v });
          }}
          {...need(rx.prescriberCouncil, 'Enter the council.')}
        />
        <TextField
          label="Registration number"
          value={rx.prescriberRegistrationNumber}
          onChange={(v) => {
            onChange({ prescriberRegistrationNumber: v });
          }}
          {...need(rx.prescriberRegistrationNumber, 'Enter the registration number.')}
        />
      </div>
      <div className={styles.row}>
        <TextField
          label="Facility that wrote it"
          value={rx.issuingFacilityName}
          onChange={(v) => {
            onChange({ issuingFacilityName: v });
          }}
          {...need(rx.issuingFacilityName, 'Enter the facility.')}
        />
        <DateField
          label="Date written"
          value={rx.prescribedOn}
          onChange={(v) => {
            onChange({ prescribedOn: v });
          }}
          {...(tried && rx.prescribedOn === null ? { errorMessage: 'Enter the date.' } : {})}
        />
      </div>
    </fieldset>
  );
}

function TodaySales({ storeId }: { storeId: string }) {
  const sales = useQuery(salesQuery(storeId));
  return (
    <section className={`${styles.section} ${screen.panel}`} aria-label="Today’s sales">
      <h2 className={styles.sectionHeading}>Today’s sales</h2>
      {sales.isPending ? <LoadingBlock label="Loading today’s sales" rows={2} /> : null}
      {sales.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void sales.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(sales.error, "Today's sales couldn't be loaded.")}
        </Notice>
      ) : null}
      {sales.isSuccess && sales.data.length === 0 ? (
        <EmptyState>No sales yet today.</EmptyState>
      ) : null}
      {sales.isSuccess && sales.data.length > 0 ? (
        <TableScroll>
          <Table flat aria-label="Today’s sales" dense>
            <TableHeader>
              <Column isRowHeader>Sale</Column>
              <Column>Time</Column>
              <Column numeric>Items</Column>
              <Column numeric>Total</Column>
              <Column>By</Column>
            </TableHeader>
            <TableBody items={sales.data}>
              {(s) => (
                <Row id={s.id}>
                  <Cell>
                    <span className="wodi-id">{s.saleNumber}</span>
                  </Cell>
                  <Cell>{formatTimeOnly(s.completedAt)}</Cell>
                  <Cell numeric>{String(s.lineCount)}</Cell>
                  <Cell numeric>{money(s.total)}</Cell>
                  <Cell>{s.completedBy}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      ) : null}
    </section>
  );
}
